import { useEffect } from 'react';
import { Nav } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Link, Route, Switch, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { getSeaLinkBasename, isInFerryBookingPage } from 'utils/helpers';
import { useCurrentBookingState } from 'utils/hooks/use_current_booking_state';
import useIsLoggedIn from 'utils/hooks/use_is_logged_in';
import { setMetaFlow } from 'redux/actions/ui/sealink';

export const NavButtons = ({ navigations, isMobile }) => {
  const { pathname } = useLocation();
  const metaFlow = useSelector((state) => state.ui.sealink.metaFlow);
  const isFerryBooking = isInFerryBookingPage(metaFlow.currentStep);

  const content = isMobile ? (
    <nav className="nav-mobile" data-testid="mobile-nav">
      <ul className="nav-user list-unstyled mb-0">
        {navigations.map((nav) => (
          <li key={nav.path}>
            <Link
              to={nav.path}
              className={classNames({
                active:
                  pathname === nav.path ||
                  (isFerryBooking && nav.path === '/bookings/ferry'),
                'pe-none opacity-25': nav.disabled,
              })}
            >
              {nav.name}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  ) : (
    <Nav
      activeKey={isFerryBooking ? '/bookings/ferry' : metaFlow.currentStep}
      className="space-x-3 py-1"
      data-testid="desktop-nav"
    >
      {navigations.map((nav) => (
        <Nav.Item key={nav.path} className="header__nav-item">
          <Nav.Link
            className={classNames('header__nav-link', {
              'pe-none opacity-25': nav.disabled,
            })}
            as={nav.externalUrl ? 'a' : Link}
            to={nav.path}
            href={nav.path}
          >
            {nav.name}
          </Nav.Link>
        </Nav.Item>
      ))}
    </Nav>
  );

  return (
    <div className="header__nav d-none d-lg-flex justify-content-end">
      <div className="header__nav-wrapper--desktop d-flex">{content}</div>
    </div>
  );
};

NavButtons.propTypes = {
  navigations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      path: PropTypes.string,
      externalUrl: PropTypes.bool,
      skip: PropTypes.bool,
      disabled: PropTypes.bool,
    })
  ),
  isMobile: PropTypes.bool,
};

export const NavigationBar = ({ isMobile }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const metaFlow = useSelector((state) => state.ui.sealink.metaFlow);
  const { isBookingPayable } = useCurrentBookingState();
  const isLoggedIn = useIsLoggedIn();
  const { pathname } = location;
  const basename = getSeaLinkBasename();

  useEffect(() => {
    if (metaFlow.currentStep !== pathname) {
      dispatch(
        setMetaFlow({
          ...metaFlow,
          currentStep: pathname,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const { features } = window.ecom.craftConfig;
  const bookingNavigations = [
    {
      name: 'Booking overview',
      path: '/bookings',
    },
    {
      name: 'Ferry',
      path: '/bookings/ferry',
      skip: !features.ferry,
    },
    {
      name: 'Accommodation',
      path: '/bookings/accommodation',
      skip: !features.accommodation,
    },
    {
      name: 'Experiences',
      path: `/${basename}/experiences`,
      externalUrl: true,
      skip: !features.experience,
    },
    {
      name: 'Checkout & Pay',
      path: '/bookings/checkout',
      disabled: !isBookingPayable,
    },
  ];

  const accountNavigations = [
    {
      name: 'Back to bookings',
      path: '/bookings/',
    },
    {
      name: 'My account',
      path: '/account',
    },
    {
      name: 'Booking history',
      path: '/account/history',
    },
    {
      name: 'Logout',
      path: `/${basename}/account/logout`,
      externalUrl: true,
    },
  ];

  return (
    <Switch>
      <Route
        path="/bookings"
        render={() => (
          <NavButtons
            navigations={bookingNavigations.filter((nav) => nav.skip !== true)}
            isMobile={isMobile}
          />
        )}
      />
      {isLoggedIn && (
        <Route
          path="/account"
          render={() => (
            <NavButtons navigations={accountNavigations} isMobile={isMobile} />
          )}
        />
      )}
    </Switch>
  );
};

NavigationBar.propTypes = {
  isMobile: PropTypes.bool,
};

export const NavigationBarWrapper = ({ isMobile = false }) => (
  <NavigationBar isMobile={isMobile} />
);

NavigationBarWrapper.propTypes = {
  isMobile: PropTypes.bool,
};
